import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TutorialService } from '@core/tutorial/tutorial.service';
import { OnboardingService } from './onboarding.service';
import { orientacaoDaRota } from './onboarding.telas';
import { OnboardingOrientacaoComponent } from './onboarding-orientacao.component';

/** A ajuda acompanha a tarefa aberta pelo roteiro e descreve a tela atual desse fluxo. */
@Component({
  selector: 'mg-onboarding-contexto',
  imports: [RouterLink, MatButton, MatIcon, OnboardingOrientacaoComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (tutorial.telaAssistida()) {
      @if (orientacao(); as tela) {
        <aside aria-label="Acompanhamento Mais Gestão">
          <div class="resumo">
            <span class="marca"><mat-icon svgIcon="support_agent" aria-hidden="true" /></span>
            <div class="texto">
              <span class="identidade">Acompanhamento Mais Gestão</span>
              <strong class="titulo">{{ tela.titulo }}</strong>
              <p>{{ tela.descricao }}</p>
            </div>
            <div class="acoes">
              @if (tutorial.temTourNaRota()) {
                <button matButton="filled" aria-haspopup="dialog" [attr.aria-expanded]="!!tutorial.tour()" (click)="tutorial.iniciarTourDaRota()">Ver esta tela passo a passo</button>
              }
              <a matButton="text" routerLink="/painel/primeiros-passos">Voltar ao roteiro</a>
            </div>
          </div>
          @for (atual of [tela]; track atual) {
            <details>
              <summary>Consultar orientações por escrito</summary>
              <div class="orientacao">
                <mg-onboarding-orientacao [orientacao]="atual" />
                @if (onboarding.erro()) {<p class="erro" role="alert">{{ onboarding.erro() }}</p>}
              </div>
            </details>
          }
        </aside>
      }
    }
  `,
  styles: `
    :host { display: block; font-family: var(--mg-fonte); color: var(--mg-text); }
    :host, :host * { box-sizing: border-box; }
    aside { margin: .8rem clamp(.75rem, 2vw, 2rem); background: var(--mg-surface); border: 1px solid var(--mg-border); border-radius: var(--mg-radius); }
    .resumo { display: flex; align-items: center; gap: .9rem; padding: 1rem 1.2rem; }
    .marca { display: grid; place-items: center; flex: 0 0 42px; height: 42px; border-radius: var(--mg-radius); background: var(--mg-acento); color: var(--mg-acento-inverse); }
    .marca mat-icon { color: inherit; }
    .texto { flex: 1; min-width: 0; }
    .identidade { display: block; margin-bottom: .3rem; color: var(--mg-text-muted); font-size: .7rem; font-weight: 600; }
    .titulo { display: block; font-size: .92rem; line-height: 1.4; font-weight: 650; }
    p { margin: .35rem 0 0; font-size: .82rem; line-height: 1.6; color: var(--mg-text-muted); }
    .acoes { display: flex; align-items: center; justify-content: flex-end; gap: .5rem; flex-wrap: wrap; }
    summary { border-top: 1px solid var(--mg-border); padding: .75rem 1.3rem; cursor: pointer; color: var(--mg-primary); font-size: .82rem; }
    summary:focus-visible { outline: 2px solid var(--mg-primary); outline-offset: 2px; }
    .orientacao { padding: 1.2rem 1.3rem; border-top: 1px solid var(--mg-border); max-width: 860px; }
    .erro { color: var(--mg-danger); }
    @media (max-width: 700px) {
      .resumo { flex-wrap: wrap; gap: .75rem; padding: 1rem; }
      .marca { align-self: flex-start; flex-basis: 36px; height: 36px; }
      .texto { flex-basis: calc(100% - 3rem); }
      .acoes { width: calc(100% - 36px - .75rem); justify-content: flex-start; margin-left: calc(36px + .75rem); }
      summary { padding: .75rem 1rem; }
      .orientacao { padding: 1rem; }
    }
  `,
})
export class OnboardingContextoComponent {
  readonly onboarding = inject(OnboardingService);
  readonly tutorial = inject(TutorialService);
  private readonly router = inject(Router);
  readonly rota = signal(this.router.url);
  readonly orientacao = computed(() => orientacaoDaRota(this.rota()));

  constructor() {
    this.router.events.pipe(filter((evento): evento is NavigationEnd => evento instanceof NavigationEnd), takeUntilDestroyed()).subscribe(evento => {
      this.rota.set(evento.urlAfterRedirects);
    });
  }
}
